import { IUser } from '../auth/user.interface';
import { IBaseModel } from '../base/base-model.interface';
import {
  createEnumOptions,
  EnumOptions,
} from '../shared/enum-option.interface';

export enum TicketState {
  Open = 'open',
  Assigned = 'assigned',
  Resolved = 'resolved',
  Reopen = 'reopen',
  Cancelled = 'cancelled',
}

export enum TicketCreatedByType {
  monitoring = 'monitoring',
  email = 'email',
  user = 'user',
  system = 'system',
}

export enum Priority {
  critical = 1,
  major = 2,
  minor = 3,
  low = 4,
}

export enum TicketType {
  nt = 'nt',
  ctt = 'ctt',
  other = 'other',
  apRequest = 'apRequest',
}

export enum FailureType {
  olt = 1,
  odb = 2,
}

export enum TechnicianDepartureType {
  none = 'none',
  authorized = 'authorized',
  unauthorized = 'unauthorized',
}

export enum ResolutionType {
  ohcChange = 'ohcChange',
  ohcRepair = 'ohcRepair',
  ontChange = 'ontChange',
  ontRepair = 'ontRepair',
  ontBulkChange = 'ontBulkChange',
  other = 'other',
  coverageAvailable = 'coverageAvailable',
  coverageUnavailable = 'coverageUnavailable',
  duplicateRequest = 'duplicateRequest',
  noResponse = 'noResponse',
}

export enum TicketSeenType {
  newTicket = 'newTicket',
  newMessages = 'newMessages',
  messagesRead = 'messagesRead',
}

export interface IAffectedOdb {
  oltName: string;
  odbId: string;
}

export const ticketActiveStates = [
  TicketState.Open,
  TicketState.Assigned,
  TicketState.Reopen,
];

export const ticketActive = (t: ITicket) =>
  ticketActiveStates.includes(t.state);

export interface ITicket extends IBaseModel<'Ticket'> {
  apId?: string;
  eic?: string;
  gpsX?: number;
  gpsY?: number;
  orderId?: string;
  extOrderId?: string;
  extCustomerId?: string;
  state: TicketState;
  proposedState?: TicketState | null;
  priority: Priority;
  externalId?: string;
  orangeId?: string;
  omName?: string;
  omPhone?: string;
  omEmail?: string;
  ispName?: string;
  ispPhone?: string;
  ispEmail?: string;
  groupIdSp?: string;
  description?: string;
  ispDescription?: string;
  ticketType?: TicketType;
  groupId?: string;
  attachments?: string[];
  failureDate?: Date;
  removalEstimatedDate?: Date;
  failureType?: FailureType;
  oltName?: string;
  affectsOdbs?: IAffectedOdb[];
  affectsFsp?: string[] | null;
  affectsUplinkFsp?: string[] | null;
  affectsIsp?: string[] | null;
  closeDate?: Date | null;
  autoClose?: boolean;
  notSeen?: boolean;
  target?: string;
  notSeenMessagesCount?: number;
  reason?: string;
  technicianDepartureType?: TechnicianDepartureType;
  resolutionType?: ResolutionType;
  affectedIspGroupIds?: string[];
  internalId: string;
  createdByProblem: boolean;
  estOutageDuration: number | null;
  spOnly?: boolean;
  createdByType?: TicketCreatedByType;
  assignedMasterUserId?: string;
  assignedUser?: IUser;
}

export const priorityOptions = createEnumOptions<Priority>({
  [Priority.critical]: {
    label: 'Kritická',
    color: 'danger',
    icon: 'exclamation',
  },
  [Priority.major]: { label: 'Vysoká', color: 'warn', icon: 'exclamation' },
  [Priority.minor]: { label: 'Stredná', color: 'success', icon: 'exclamation' },
  [Priority.low]: { label: 'Nízka', color: 'none', icon: 'exclamation' },
  _default: { label: '-', color: 'none', icon: 'minus' },
});

export const ticketStateOptions = createEnumOptions<TicketState>({
  [TicketState.Open]: { label: 'Otvorený', color: 'danger' },
  [TicketState.Assigned]: { label: 'Priradený', color: 'primary' },
  [TicketState.Resolved]: { label: 'Vyriešený', color: 'success' },
  [TicketState.Reopen]: { label: 'Znovu-otvorený', color: 'warn' },
  [TicketState.Cancelled]: { label: 'Zrušený', color: 'none' },
  _default: { label: '-' },
});

export const ticketTypeOptions = createEnumOptions<TicketType>({
  [TicketType.nt]: { label: 'Network ticket' },
  [TicketType.ctt]: { label: 'Client trouble ticket' },
  [TicketType.apRequest]: { label: 'Dopyt na pokrytie' },
  [TicketType.other]: { label: 'Iné' },
  _default: { label: '-' },
});

export const failureTypeOptions = createEnumOptions<FailureType>({
  [FailureType.odb]: { label: 'ODB' },
  [FailureType.olt]: { label: 'OLT' },
  _default: { label: '-' },
});

export const ticketCreatedByTypeOptions =
  createEnumOptions<TicketCreatedByType>({
    [TicketCreatedByType.monitoring]: { label: 'Monitoring' },
    [TicketCreatedByType.email]: { label: 'Email' },
    [TicketCreatedByType.user]: { label: 'Používateľ' },
    [TicketCreatedByType.system]: { label: 'Systém' },
    _default: { label: '-' },
  });

export const technicianDepartureTypeOptions =
  createEnumOptions<TechnicianDepartureType>({
    [TechnicianDepartureType.none]: { label: 'Bez výjazdu' },
    [TechnicianDepartureType.authorized]: {
      label: 'Oprávnený servisný výjazd',
    },
    [TechnicianDepartureType.unauthorized]: {
      label: 'Márny výjazd pri riešení  poruchy',
    },
    _default: { label: '-' },
  });

export const resolutionTypeOptions = createEnumOptions({
  [ResolutionType.ohcChange]: {
    label:
      'Oprava inštalácie / výmena DOP - Zahŕňa prípady, kedy je potrebné vymeniť optický kábel',
  },
  [ResolutionType.ohcRepair]: {
    label: 'Oprava inštalácie / oprava DOP bez výmeny optického kábla',
  },
  [ResolutionType.ontChange]: {
    label:
      'Výmena ONT s prácou - Výjazd, oprava/výmena, overenie funkčnosti služby',
  },
  [ResolutionType.ontRepair]: { label: 'Oprava ONT' },
  [ResolutionType.ontBulkChange]: { label: 'Hromadná výmena ONT' },
  [ResolutionType.other]: { label: 'Iný servisný úkon' },
  _default: { label: '-' },
});

export const apRequestResolutionTypeOptions = createEnumOptions({
  [ResolutionType.coverageAvailable]: { label: 'Pokrytie dostupné' },
  [ResolutionType.coverageUnavailable]: { label: 'Pokrytie nedostupné' },
  [ResolutionType.duplicateRequest]: { label: 'Duplicitný dopyt' },
  [ResolutionType.noResponse]: { label: 'Bez odpovede' },
  _default: { label: '-' },
});

export const allResolutionTypeOptions = new Map<string, EnumOptions>([
  ...resolutionTypeOptions,
  ...apRequestResolutionTypeOptions,
]);

export interface IAffectedOrder {
  id: string;
  extOrderId: string;
}

export const ticketSeenOptions = createEnumOptions<TicketSeenType>({
  [TicketSeenType.newTicket]: { label: 'Nový ticket', color: 'success' },
  [TicketSeenType.newMessages]: { label: 'Nové správy', color: 'primary' },
  [TicketSeenType.messagesRead]: {
    label: 'Správy sú prečítané',
    color: 'none',
  },
  _default: { label: '-' },
});
